import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import myAxios from '../_services/axiositeachWrapper';
import Config from '../_config/config';

/**
 * Framework-level auto-translation via MutationObserver.
 *
 * When the language is not English, fetches the full { en: translated } dictionary
 * from the API and replaces matching text nodes in the DOM automatically.
 * No per-component t() calls needed — works across all pages.
 */
const AutoTranslate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const dictRef = useRef<Record<string, string>>({});
  const sortedKeysRef = useRef<string[]>([]);
  const observerRef = useRef<MutationObserver | null>(null);
  const originalTextsRef = useRef(new WeakMap<Text, string>());
  const translatingRef = useRef(false);
  const rafIdRef = useRef<number>(0);

  // Cache: lang → dictionary, so switching back doesn't re-fetch
  const dictCacheRef = useRef<Record<string, Record<string, string>>>({});

  const currentLang = i18n.language;

  /** Translate a single text node if it matches the dictionary */
  const translateNode = useCallback((node: Text) => {
    const dict = dictRef.current;
    if (!sortedKeysRef.current.length) return;

    // Store original text on first encounter
    if (!originalTextsRef.current.has(node)) {
      originalTextsRef.current.set(node, node.textContent || '');
    }

    const original = originalTextsRef.current.get(node) || '';
    const trimmed = original.trim();
    if (!trimmed) return;

    // Exact match (most common — buttons, labels, headers)
    if (dict[trimmed]) {
      // Preserve surrounding whitespace from original
      const leading = original.match(/^\s*/)?.[0] || '';
      const trailing = original.match(/\s*$/)?.[0] || '';
      node.textContent = leading + dict[trimmed] + trailing;
      return;
    }

    // Partial replacement: replace all known English phrases within longer text
    let result = original;
    for (const key of sortedKeysRef.current) {
      if (result.includes(key)) {
        result = result.split(key).join(dict[key]);
      }
    }
    if (result !== original) {
      node.textContent = result;
    }
  }, []);

  /** Restore a text node to its original English content */
  const restoreNode = useCallback((node: Text) => {
    const original = originalTextsRef.current.get(node);
    if (original !== undefined) {
      node.textContent = original;
    }
  }, []);

  /** Walk all text nodes under a root element */
  const walkTextNodes = useCallback((root: Node, fn: (node: Text) => void) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    while (walker.nextNode()) {
      const textNode = walker.currentNode as Text;
      // Skip script/style tags and empty nodes
      const parent = textNode.parentElement;
      if (!parent) continue;
      const tag = parent.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEXTAREA') continue;
      // Skip input-like elements
      if (parent.isContentEditable) continue;
      fn(textNode);
    }
  }, []);

  /** Translate nodes while observer is paused to avoid self-triggered mutations */
  const translateWithObserverPaused = useCallback((root: Node) => {
    observerRef.current?.disconnect();
    translatingRef.current = true;
    walkTextNodes(root, translateNode);
    translatingRef.current = false;
    // Re-observe after our own mutations are done
    if (observerRef.current) {
      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }, [walkTextNodes, translateNode]);

  /** Fetch dictionary and translate entire page */
  useEffect(() => {
    // Disconnect any existing observer while we re-setup
    observerRef.current?.disconnect();
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = 0;
    }

    if (currentLang === 'en') {
      // Restore all nodes to English
      walkTextNodes(document.body, restoreNode);
      dictRef.current = {};
      sortedKeysRef.current = [];
      return;
    }

    const setupTranslation = (data: Record<string, string>) => {
      dictRef.current = data;
      // Pre-compute sorted keys once (longest first for partial matching)
      sortedKeysRef.current = Object.keys(data).sort((a, b) => b.length - a.length);

      // Translate all existing text nodes (observer disconnected during this)
      translatingRef.current = true;
      walkTextNodes(document.body, translateNode);
      translatingRef.current = false;

      // Collect pending nodes between rAF frames for batched translation
      let pendingNodes: Node[] = [];

      const flushPending = () => {
        rafIdRef.current = 0;
        if (!pendingNodes.length) return;
        const nodes = pendingNodes;
        pendingNodes = [];

        // Pause observer while we translate to avoid feedback loop
        observerRef.current?.disconnect();
        translatingRef.current = true;
        for (const n of nodes) {
          if (n.nodeType === Node.TEXT_NODE) {
            translateNode(n as Text);
          } else if (n.nodeType === Node.ELEMENT_NODE) {
            walkTextNodes(n, translateNode);
          }
        }
        translatingRef.current = false;
        // Re-observe
        if (observerRef.current) {
          observerRef.current.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      };

      // Watch for new/changed nodes (React renders, lazy loads, etc.)
      const observer = new MutationObserver((mutations) => {
        // Skip mutations caused by our own translation work
        if (translatingRef.current) return;

        for (const mutation of mutations) {
          // New nodes added
          for (let i = 0; i < mutation.addedNodes.length; i++) {
            pendingNodes.push(mutation.addedNodes[i]);
          }
          // Text content changed on existing node
          if (
            mutation.type === 'characterData' &&
            mutation.target.nodeType === Node.TEXT_NODE
          ) {
            const node = mutation.target as Text;
            const current = node.textContent || '';
            const original = originalTextsRef.current.get(node);
            // If React re-rendered the original English text, re-translate
            if (original && current === original) {
              pendingNodes.push(node);
            } else if (!original) {
              // New text we haven't seen before
              pendingNodes.push(node);
            }
          }
        }

        // Batch via rAF — coalesces rapid React re-renders into one pass
        if (pendingNodes.length && !rafIdRef.current) {
          rafIdRef.current = requestAnimationFrame(flushPending);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      observerRef.current = observer;
    };

    const fetchAndTranslate = async () => {
      try {
        // Use cached dictionary if available
        if (dictCacheRef.current[currentLang]) {
          setupTranslation(dictCacheRef.current[currentLang]);
          return;
        }

        const resp: any = await myAxios.get(
          `${Config.nodeSiteUrl}v1/i18n/translations`,
          { params: { lang: currentLang } }
        );
        const data = resp?.data?.data;
        if (!data || !Object.keys(data).length) return;

        // Cache for future switches
        dictCacheRef.current[currentLang] = data;
        setupTranslation(data);
      } catch (err) {
        console.warn('[AutoTranslate] Failed to fetch translations:', err);
      }
    };

    fetchAndTranslate();

    return () => {
      observerRef.current?.disconnect();
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
      }
    };
  }, [currentLang, translateNode, restoreNode, walkTextNodes, translateWithObserverPaused]);

  return <>{children}</>;
};

export default AutoTranslate;
